import debug from 'debug';
import { v4 as uuidv4 } from 'uuid';
import globals from '@/globals';
import router from '@/router';
import ParcelexApi from '@/services/parcelex';
import eventService from '../../services/event';
import CheckoutService from './checkout.service';
import CheckoutLib from './checkout.lib';
import getHandler from '../../lib/handleEvent';
import StoreApi from '../../services/store';
import logger from '../../lib/logger';
import UtilMixin from '../../mixins/util';
import moment from '../../lib/moment-ptbr';
import deniedStrategy from './utils/deniedStrategy';

// to move prod to homolog
let isHomolog = false;
// to verify if is on homolog env
let isOnHomolog = false;

function saveState(state) {
  if (state && state.checkoutId) {
    const toSave = { ...state };
    delete toSave.defaults;
    delete toSave.summarySkeleton;
    delete toSave.downPaymentValueSkeleton;
    delete toSave.installmentValueSkeleton;
    delete toSave.eventHandler;
    delete toSave.config;
    StoreApi
      .post(`/store/${state.checkoutId}`, toSave)
      .catch(logger.error);
  }
}

const skeletonTimeout = globals().skeleton.checkout.timeout;

const log = debug('store:checkout');

function putHomologInOptionId(optionsRes) {
  log('putHomologInOptionId', 'optionsResIn', optionsRes);
  if (isHomolog) {
    if (optionsRes && optionsRes.data && Array.isArray(optionsRes.data.downPaymentOptions)) {
      optionsRes.data.downPaymentOptions
        .forEach((dpOpt) => Array.isArray(dpOpt.options) && dpOpt.options.forEach((opt) => {
          // eslint-disable-next-line no-param-reassign
          opt._id = `homolog-${opt._id}`;
        }));
    }
  }
  log('putHomologInOptionId', 'optionsResOut', optionsRes);
}

function isApiOnHomolog() {
  const { apiUrl } = globals();
  return apiUrl.includes('homolog');
}

const handleEvent = getHandler(log);

function getTotalProductPrice(products) {
  return products.reduce((acc, curr) => {
    const newVal = acc + curr.value * curr.amount;
    return newVal;
  }, 0);
}

function calculate(value, interest, installments) {
  const pv = value;
  // eslint-disable-next-line max-len
  const pmt = pv * ((((1 + interest) ** installments) * interest) / (((1 + interest) ** installments) - 1));
  return {
    installments,
    interest,
    pmt,
  };
}

function fixed2(raw) {
  return parseFloat(raw.toFixed(2));
}

function findInputErrors(initData, isHomologEnv) {
  const { user, order } = initData;
  const { shipping } = order || {};

  const invalidInputs = UtilMixin.methods.getInvalidInputs({
    email: user && user.email,
    cpf: user && user.cpf,
    phone: user && user.phone,
    shipping,
    isHomolog: isHomologEnv,
  });

  return invalidInputs;
}

function checkValidationError({ errorMsg, validateField }) {
  if (!validateField) return false;

  const validateFieldsOf = {
    shipping: [
      'shipping',
      'zipCode',
      'addressStreet',
      'addressNumber',
      'addressComplement',
      'city',
      'state',
    ],
  };

  if (!validateFieldsOf[validateField]) return false;

  const fieldError = errorMsg.substring(errorMsg.indexOf('child'), errorMsg.indexOf('fails'))
    .trim()
    .replace(/"/g, '')
    .split(' ')[1];

  return validateFieldsOf[validateField].includes(fieldError);
}

export default {
  namespaced: true,
  state: {
    apiKey: '',
    sessionId: null,
    page: '',
    summarySkeleton: false,
    downPaymentValueSkeleton: false,
    installmentValueSkeleton: false,
    eventHandler: null,
    config: null,

    defaults: {
      interest: 0.03,
      installments: 12,
    },
    device: null,
    user: null,
    order: null,
    showScript: null,
    simulation: null,
    preApproved: null,
    totalProductPrice: null,
    totalAmount: null,
    preview: null,
    options: {},
    downPaymentOptions: null,
    installmentIndexSelected: null,
    downPaymentIndexSelected: 0,
    dueDateIndexSelected: 0,
    checkoutId: null,
    inputErrors: null,
    trackId: null,
    context: null,
    currentMethod: null,
    pxToken: null,
    hasPreCondition: false,
    retailerName: 'default',
    hasGracePeriod: false,
  },
  actions: {
    needAuthenticate({ state }) {
      handleEvent({ state, ev: 'onAuthenticateUser' });
    },
    reload({ state, dispatch }, newState) {
      if (newState && newState.retailerName) {
        dispatch('theme/setTheme', newState.retailerName, { root: true });
      }

      Object.keys(newState)
        .forEach((key) => {
          state[key] = newState[key];
        });
    },
    async windowResized({ state }, { width, height }) {
      handleEvent({ state, ev: 'onWindowResize', data: { width, height } });
    },
    async checkoutValidate({ state }, checkoutId) {
      if (!state.checkoutId
      || !checkoutId
      || state.checkoutId !== checkoutId) {
        throw new Error('wrong checkout cache');
      }
    },
    async error({ state }) {
      handleEvent({ state, ev: 'onError' });
    },
    async resetLoading({ commit }) {
      commit('toggleSummarySkeleton', false);
      commit('toggleDownPaymentValueSkeleton', false);
      commit('toggleInstallmentValueSkeleton', false);
    },
    async ready({ commit, state }) {
      handleEvent({ state, ev: 'onReady' });
      commit('toggleSummarySkeleton', false);
      commit('toggleDownPaymentValueSkeleton', false);
      commit('toggleInstallmentValueSkeleton', false);
    },
    async shippingChanged({ state, commit, getters }, shipping) {
      const { page } = state;
      commit('setShipping', shipping);
      if (page !== 'checkout-approved') {
        const isValid = UtilMixin.methods.isValidShipping(shipping);
        if (isValid) {
          const newInputErrors = state.inputErrors ? [...state.inputErrors] : [];
          const index = newInputErrors.indexOf('shipping');
          if (index > -1) {
            newInputErrors.splice(index, 1);
          }
          commit('setInputErrors', newInputErrors);
          commit('goToPage', 'checkout-info');
          return;
        }
      }
      const { checkoutId, apiKey } = state;
      commit('toggleSummarySkeleton', true);
      commit('toggleDownPaymentValueSkeleton', true);
      commit('toggleInstallmentValueSkeleton', true);
      if (checkoutId) {
        const { data } = await CheckoutService
          .updateShipping({ shipping, checkoutId, apiKey });
        log('shippingChanged', data);
        commit('setDownPaymentOptions', data);
        commit('toggleSummarySkeleton', false);
        commit('toggleDownPaymentValueSkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      } else {
        setTimeout(() => {
          commit('toggleSummarySkeleton', false);
          commit('toggleDownPaymentValueSkeleton', false);
          commit('toggleInstallmentValueSkeleton', false);
        }, skeletonTimeout);
      }
      const data = {
        downPaymentOptions: state.downPaymentOptions,
        downPaymentOptionSelected: getters.downPaymentSelected,
        installmentOptionSelected: getters.installmentSelected,
        isApproved: getters.isApproved,
      };
      handleEvent({ state, ev: 'onShippingChanged', data });
    },
    async checkoutDataChanged({ state, dispatch }, updateData) {
      log('checkoutDataChanged', updateData);
      dispatch('init', updateData);
      handleEvent({ state, ev: 'onCheckoutDataChanged' });
    },
    async getLastState({ state, commit }, { checkoutId, sessionId }) {
      if (state.checkoutId && state.checkoutId === checkoutId) {
        if (sessionId) commit('setSessionId', sessionId);
        log('cached checkoutId');
        if (state.page) commit('goToPage', state.page);
        handleEvent({ state, ev: 'onSuccessCache' });
      } else {
        log('cached fail');
        handleEvent({ state, ev: 'onFailCache' });
      }
    },
    async init({ rootState, state, commit, getters, dispatch }, initData) {
      commit('setHasPreCondition', false);
      dispatch('theme/setTheme', 'default', { root: true });
      const { retailerName } = initData;
      if (retailerName) dispatch('theme/setTheme', retailerName, { root: true });
      if (initData.order.products
        .find((el) => el.name === 'Lorem ipsum dolor sit amet, consectetur adipiscing elit')) {
        isHomolog = true;
        ParcelexApi.defaults.baseURL = ParcelexApi.defaults.baseURL
          .replace('api.', 'api-homolog.');
      }
      isOnHomolog = isApiOnHomolog();
      log('isHomolog', isHomolog);
      try {
        const errorFound = findInputErrors(initData, isOnHomolog);
        log('init', state);
        log('data', initData);
        const totalProductPrice = getTotalProductPrice(initData.order.products);
        commit('setInitData', {
          ...initData,
          shipping: initData.order.shipping || {},
          totalProductPrice: Number.isNaN(totalProductPrice)
            ? 0 : totalProductPrice,
          sessionId: uuidv4(),
        });
        dispatch('setHasGracePeriod', {
          hasGracePeriod: initData.hasGracePeriod || false,
        });
        if (errorFound && errorFound.length > 0) {
          commit('setInputErrors', errorFound);
          commit('goToPage', 'checkout-info');
          return;
        }
        commit('setInputErrors', null);
        const { order } = initData;
        const { products } = order;
        const product = (products && products[0]) || {};

        const currentTrackId = rootState.track && rootState.track.id;
        const trackId = initData.trackId || currentTrackId;

        const { data } = await CheckoutService
          .preview({
            apiKey: initData.apiKey,
            cpf: initData.user.cpf,
            phone: initData.user.phone,
            email: initData.user.email,
            shipping: initData.order.shipping.value,
            totalProductPrice: getTotalProductPrice(initData.order.products),
            checkoutId: initData.checkoutId || null,
            skuNumber: product.id,
            productName: product.name,
            productDescription: product.description,
            seller: product.seller,
            productUrl: product.url,
            productImage: product.image,
            categoryName: product.category,
            categoryId: product.categoryId,
            bypassEvent: true,
            trackId,
          });
        log('preview', data);
        commit('setPreview', data);

        dispatch('track/setId', { id: data.trackId }, { root: true });

        if (data.preApproved) {
          commit('setHasPreCondition', true);
          const optionsRes = await CheckoutService.options({
            apiKey: state.apiKey,
            env: state.env,
            device: state.device,
            user: state.user,
            order: state.order,
            sessionId: state.sessionId,
            trackId: rootState.track && rootState.track.id,
            hasGracePeriod: state.hasGracePeriod,
            pxToken: state.pxToken,
          });
          putHomologInOptionId(optionsRes);
          commit('setOptions', { ...optionsRes.data, sessionId: state.sessionId });
          if (getters.isApproved) {
            commit('goToPage', `checkout-approved${state.hasGracePeriod ? '-grace' : ''}`);
          } else {
            commit('goToPage', 'checkout-denied');
          }
        } else if (data.showScript === false) {
          commit('setHasPreCondition', true);
          commit('setOptions', {
            result: 'NO_ACTION_PREVIEW',
            refusalReason: data.refusalReason,
          });
          commit('goToPage', 'checkout-denied');
        } else {
          commit('goToPage', 'checkout-info');
        }
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 400) {
          if (error.response.data === 'invalid email') {
            commit('setInputErrors', ['email']);
          } else if (checkValidationError({ errorMsg: error.response.data, validateField: 'shipping' })) {
            commit('setInputErrors', ['shipping']);
          } else {
            commit('setInputErrors', ['genericError']);
          }
          commit('goToPage', 'checkout-info');
        } else {
          throw error;
        }
      }
    },
    async sendConfirmationCode({ state, commit }, method) {
      commit('setCurrentMethod', method);
      try {
        await CheckoutService.createUser({
          cpf: state.user.cpf,
          email: state.user.email,
          phone: state.user.phone,
          zipCode: state.order.shipping.zipCode,
          apiKey: state.apiKey,
        });
      } catch (error) {
        console.log(error);
      } finally {
        await CheckoutService.sendConfirmationCode({
          cpf: state.user.cpf,
          isSecurityCode: true,
          resend: false,
          method,
        });
      }
    },
    async confirmSecurityCode({ state, commit }, securityCode) {
      const { data } = await CheckoutService.confirmSecurityCode({
        cpf: state.user.cpf,
        token: securityCode,
      });

      commit('setUniqueToken', data.pxToken);
    },
    emitEvent({ rootState, state }, {
      type,
      page = 'checkout',
    }) {
      const { downPaymentOptions, installmentIndexSelected } = state;
      const index = installmentIndexSelected || 0;
      let optionId = null;
      if (downPaymentOptions && index >= 0) {
        const downPayment = downPaymentOptions[index];
        if (downPayment && downPayment.options && Array.isArray(downPayment.options)) {
          optionId = downPayment.options[installmentIndexSelected]
           && downPayment.options[installmentIndexSelected]._id;
        }
      }

      eventService.logCheckoutEvents({
        type,
        data: {
          ...state,
          page,
          optionId,
          trackId: rootState.track && rootState.track.id,
        },
      }).catch(logger.error);
    },
    async pay({ state, getters }) {
      const data = {
        downPaymentOptions: state.downPaymentOptions,
        downPaymentOptionSelected: getters.downPaymentSelected,
        installmentOptionSelected: getters.installmentSelected,
      };
      handleEvent({ state, ev: 'onPay', data });
    },
    async goToPage({ commit }, page) {
      commit('goToPage', page);
    },
    async getOptions({ rootState, state, commit, dispatch }) {
      try {
        const { data } = await CheckoutService.options({
          apiKey: state.apiKey,
          env: state.env,
          device: state.device,
          user: state.user,
          order: state.order,
          sessionId: state.sessionId,
          trackId: rootState.track && rootState.track.id,
          hasGracePeriod: state.hasGracePeriod,
          pxToken: state.pxToken,
        });
        putHomologInOptionId({ data });
        const {
          trackId,
        } = data;

        const currentTrackId = rootState.track && rootState.track.id;
        if (trackId && trackId !== currentTrackId) {
          dispatch('track/setId', { id: trackId }, { root: true });
        }
        commit('setOptions', data);
      } catch (error) {
        if (error && error.response && error.response.status === 400) {
          if (checkValidationError({ errorMsg: error.response.data, validateField: 'shipping' })) {
            commit('setInputErrors', ['shipping']);
          } else {
            commit('setInputErrors', ['genericError']);
          }
          commit('goToPage', 'checkout-info');
        } else {
          throw error;
        }
      }
    },
    async setContext({ commit }, context) {
      commit('setContext', context);
    },
    setInstallmentIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        downPaymentIndexSelected,
        dueDateIndexSelected,
        hasGracePeriod,
      } = state;
      if (downPaymentOptions && downPaymentIndexSelected >= 0 && index >= 0) {
        if (downPaymentOptions) {
          const downPayment = downPaymentOptions[downPaymentIndexSelected];
          if (!downPayment) return;

          if (hasGracePeriod && downPayment.dueDateOptions && downPayment.dueDateOptions.length) {
            const dueDateOption = downPayment.dueDateOptions[dueDateIndexSelected];
            const installment = dueDateOption.installmentOptions[index];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          } else if (downPayment.options && downPayment.options.length) {
            const installment = downPayment.options[index];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }
      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
      }, skeletonTimeout);
      commit('setInstallmentIndex', index);
    },
    setDownPaymentIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        installmentIndexSelected,
        dueDateIndexSelected,
        hasGracePeriod,
      } = state;
      if (downPaymentOptions && index >= 0) {
        const downPayment = downPaymentOptions[index];
        handleEvent({ state, ev: 'onDownPaymentChanged', data: { downPayment } });

        if (typeof installmentIndexSelected === 'number' && installmentIndexSelected >= 0) {
          if (hasGracePeriod && downPayment.dueDateOptions && downPayment.dueDateOptions.length) {
            const dueDateOption = downPayment.dueDateOptions[dueDateIndexSelected];
            const installment = dueDateOption.installmentOptions[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          } else if (downPayment.options && downPayment.options.length) {
            const installment = downPayment.options[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }
      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
        commit('toggleInstallmentValueSkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDownPaymentIndex', index);
    },
    setDueDateIndex({ commit, state }, index) {
      const {
        downPaymentOptions,
        downPaymentIndexSelected,
        installmentIndexSelected,
      } = state;
      if (downPaymentOptions && downPaymentIndexSelected >= 0 && index >= 0) {
        const downPayment = downPaymentOptions[downPaymentIndexSelected];
        if (downPayment && downPayment.dueDateOptions) {
          const dueDateOption = downPayment.dueDateOptions[index];
          handleEvent({ state, ev: 'onDueDateChanged', data: { dueDateOption } });

          if (typeof installmentIndexSelected === 'number'
            && installmentIndexSelected >= 0) {
            const installment = dueDateOption.installmentOptions[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }

      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleSummarySkeleton', true);
        commit('toggleInstallmentValueSkeleton', true);
      }
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDueDateIndex', index);
    },
    setFallbackData({ commit }, {
      downPaymentOptions,
      downPaymentIndexSelected,
      installmentIndexSelected,
      checkoutId,
      hasGracePeriod,
    }) {
      commit('setFallbackData', {
        downPaymentOptions,
        downPaymentIndexSelected,
        installmentIndexSelected,
        checkoutId,
        hasGracePeriod,
      });
    },
    setHasGracePeriod({ commit }, hasGracePeriod) {
      commit('setHasGracePeriod', hasGracePeriod);
    },
  },
  mutations: {
    setCurrentMethod(state, method) {
      state.currentMethod = method;
    },
    setUniqueToken(state, pxToken) {
      state.pxToken = pxToken;
    },
    setHasPreCondition(state, hasPreCondition) {
      state.hasPreCondition = hasPreCondition;
    },
    toggleSummarySkeleton(state, summarySkeleton) {
      state.summarySkeleton = summarySkeleton;
    },
    toggleDownPaymentValueSkeleton(state, downPaymentValueSkeleton) {
      state.downPaymentValueSkeleton = downPaymentValueSkeleton;
    },
    toggleInstallmentValueSkeleton(state, installmentValueSkeleton) {
      state.installmentValueSkeleton = installmentValueSkeleton;
    },
    setShipping(state, shipping) {
      state.order.shipping = shipping;
    },
    setSessionId(state, sessionId) {
      state.sessionId = sessionId;
    },
    setDownPaymentOptions(state, downPaymentOptions) {
      state.downPaymentOptions = downPaymentOptions;
    },
    goToPage(state, page) {
      handleEvent({ state, ev: 'onChangePage', data: { page } });
      router.replace({ name: page }).catch((err) => log('router error', err));
      state.page = page;
    },
    setContext(state, context) {
      const { config, eventHandler } = CheckoutLib.getContextVariables(context);
      state.eventHandler = eventHandler;
      state.config = config;
      state.context = context;
    },
    setDownPaymentIndex(state, index) {
      state.downPaymentIndexSelected = index;
      saveState(state);
    },
    setInstallmentIndex(state, index) {
      state.installmentIndexSelected = index;
      saveState(state);
    },
    setInitData(state, {
      apiKey,
      device,
      user,
      totalProductPrice,
      order,
      shipping,
      checkoutId,
      sessionId,
      retailerName,
    }) {
      state.apiKey = apiKey;
      state.device = device;
      state.user = user;
      state.order = order;
      state.totalProductPrice = totalProductPrice;
      state.totalAmount = state.totalProductPrice + shipping.value;
      state.checkoutId = checkoutId;
      state.sessionId = sessionId;
      state.options = {};
      state.retailerName = retailerName || 'default';
    },
    setPreview(state, preview) {
      handleEvent({ state, ev: 'onPreviewChanged', data: { preview } });
      state.preview = preview;
      state.showScript = preview.showScript;
      state.simulation = preview.simulation;
      state.preApproved = preview.preApproved;
    },
    setOptions(state, options) {
      handleEvent({ state, ev: 'onOptionsChanged', data: { options } });
      state.checkoutId = options.checkoutId;
      state.options = options;
      state.downPaymentOptions = options.downPaymentOptions;
      state.hasGracePeriod = options.hasGracePeriod;
    },
    setPrice(state, price) {
      state.price = price;
    },
    setInputErrors(state, inputErrors) {
      state.inputErrors = inputErrors;
    },
    setFallbackData(state, {
      downPaymentOptions,
      downPaymentIndexSelected,
      installmentIndexSelected,
      checkoutId,
      hasGracePeriod,
    }) {
      state.downPaymentOptions = downPaymentOptions;
      state.downPaymentIndexSelected = downPaymentIndexSelected;
      state.installmentIndexSelected = installmentIndexSelected;
      state.checkoutId = checkoutId;
      state.hasGracePeriod = hasGracePeriod;
    },
    setHasGracePeriod(state, { hasGracePeriod }) {
      state.hasGracePeriod = hasGracePeriod;
    },
    setDueDateIndex(state, index) {
      state.dueDateIndexSelected = index;
      saveState(state);
    },
  },
  getters: {
    downPaymentSelected({ downPaymentOptions, downPaymentIndexSelected }) {
      if (!downPaymentOptions) return null;
      return downPaymentOptions[downPaymentIndexSelected];
    },
    installmentSelected(state, getters) {
      const { downPaymentSelected } = getters;
      const { installmentIndexSelected, dueDateIndexSelected, hasGracePeriod } = state;
      if (!downPaymentSelected) return null;
      if (hasGracePeriod) {
        if (!downPaymentSelected.dueDateOptions) return null;
        return downPaymentSelected
          .dueDateOptions[dueDateIndexSelected]
          .installmentOptions[installmentIndexSelected];
      }
      if (!downPaymentSelected.options) return null;
      return downPaymentSelected.options[installmentIndexSelected];
    },
    dueDateSelected: (state, getters) => {
      const { downPaymentSelected } = getters;
      const { dueDateIndexSelected } = state;
      if (!downPaymentSelected) return null;
      if (!downPaymentSelected.dueDateOptions) return null;
      return downPaymentSelected.dueDateOptions[dueDateIndexSelected];
    },
    dateOptions: ({ downPaymentOptions }) => {
      const [firstOption] = downPaymentOptions;
      return firstOption.dueDateOptions.map(
        (dueDateOption) => moment(dueDateOption.gracePeriodDueDate, 'YYYY-MM-DD')
          .format('D-MMM')
          .toUpperCase()
          .split('-'),
      );
    },
    simulation: ({ defaults, preview, totalAmount }) => {
      const simulatedInterest = preview
        && preview.simulation && preview.simulation.interest;
      const installmentsNumber = preview
      && preview.simulation && preview.simulation.installmentsNumber;
      const {
        installments,
        pmt,
        interest,
      } = calculate(
        totalAmount,
        simulatedInterest || defaults.interest,
        installmentsNumber || defaults.installments,
      );
      return {
        pmt: Number.isNaN(pmt) ? '-' : fixed2(pmt),
        totalValue: Number.isNaN(pmt) || Number.isNaN(installments)
          ? '-' : fixed2(pmt * installments),
        numberOfInstallments: Number.isNaN(installments) ? '-' : installments,
        interest,
      };
    },
    isApproved: ({ options }) => {
      if (!options) return null;
      return options.result === 'APPROVED';
    },
    ccbUrl: (state, getters) => {
      const { user, order } = state;
      const { installmentSelected } = getters;
      const { shipping } = order || {};

      const basicPath = 'https://cdn.parcelex.com.br/minha-ccb.html';
      if (!user || !order || !installmentSelected || !shipping) return basicPath;

      const { _id } = installmentSelected;
      const { apiUrl } = globals();

      return `${apiUrl}/public/ccb/${_id.toString()}`;
    },
    titleDeniedMessage: ({ options }) => deniedStrategy.titleDeniedMessage({ options }),
    infoDeniedMessage: ({ options }) => deniedStrategy.infoDeniedMessage({ options }),
  },
};
